import axios from "axios";
import { toast } from "sonner";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth is carried by an httpOnly cookie (fm_token) set by the backend on
// login/register/claim. withCredentials makes the browser send it on every
// call. The token is never readable from JS, which closes the XSS
// token-theft vector that localStorage had.
export const api = axios.create({ baseURL: API, withCredentials: true });

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

// Double-submit CSRF: echo the readable fm_csrf cookie back in a custom header
// on every state-changing request. The backend rejects cookie-authenticated
// mutations whose header doesn't match the cookie.
const MUTATING = new Set(["post", "put", "patch", "delete"]);
api.interceptors.request.use((config) => {
  if (MUTATING.has((config.method || "get").toLowerCase())) {
    const t = readCookie("fm_csrf");
    if (t) config.headers["X-CSRF-Token"] = t;
  }
  return config;
});

// Session invalidation: a suspended retailer (or any suspended user) keeps a
// cryptographically-valid JWT cookie, but the backend now returns 403 with a
// "suspended"/"paused" message on every authenticated call. Detect that,
// clear the dead session and bounce the user to the right login screen.
let _handlingSuspension = false;
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (
      status === 403 &&
      typeof detail === "string" &&
      /suspended|paused/i.test(detail) &&
      !_handlingSuspension
    ) {
      _handlingSuspension = true;
      try { toast.error(detail, { id: "fm-suspended" }); } catch (e) { /* noop */ }
      const onStaff = /^\/(admin|retailer)/.test(window.location.pathname);
      api.post("/auth/logout").catch(() => {}).finally(() => {
        setTimeout(() => window.location.assign(onStaff ? "/retailer/login" : "/account"), 900);
      });
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

// Categories temporarily not offered (keep in sync with backend deps.DISABLED_FLOORING_TYPES).
export const DISABLED_FLOORING = new Set(["artificial_grass"]);

export const ALL_FLOORING_TYPES = [
  { value: "carpet", label: "Carpet" },
  { value: "vinyl", label: "Vinyl / Lino" },
  { value: "laminate", label: "Laminate" },
  { value: "lvt", label: "LVT" },
  { value: "artificial_grass", label: "Artificial Grass" },
  { value: "carpet_tiles", label: "Carpet Tiles" },
  { value: "other", label: "Other" },
];
export const FLOORING_TYPES = ALL_FLOORING_TYPES.filter((t) => !DISABLED_FLOORING.has(t.value));

export const STOCK_TYPES = [
  { value: "remnant", label: "Remnant", phrase: "a remnant" },
  { value: "roll_end", label: "Roll end", phrase: "a roll end" },
  { value: "surplus", label: "Surplus packs / stock", phrase: "surplus stock" },
];

export const CONDITIONS = [
  { value: "new", label: "New (roll end)" },
  { value: "like_new", label: "Like new" },
  { value: "good", label: "Good" },
];

export function floorLabel(t) {
  return FLOORING_TYPES.find((x) => x.value === t)?.label || t;
}

export function condLabel(c) {
  return { new: "New", like_new: "Like new", good: "Good" }[c] || c;
}

// Like-for-like saving vs the retailer-stated retail price (same price basis,
// same quantity). Returns null unless the comparison is genuinely higher.
export function priceSaving(price, retail, qty = 1) {
  const p = Number(price), r = Number(retail), q = Number(qty) || 1;
  if (!(p > 0) || !(r > p)) return null;
  return { price: p * q, retail: r * q, saved: (r - p) * q, pct: Math.round(((r - p) / r) * 100) };
}

export function priceBasisLabel(l) {
  if (l?.matching_mode !== "coverage") return "whole piece";
  return l.price_basis === "per_pack" ? "per pack" : `for all ${l.packs_available} packs`;
}

export function gbp(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);
}
